import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { RotateCcw, X } from "lucide-react";
import { DriveDeskAIIcon } from "@/components/icons/DriveDeskAIIcon";
import { useAIAssistant } from "@/components/ai-assistant/useAIAssistant";
import { AIChatMessageList } from "@/components/ai-assistant/AIChatMessageList";
import { AIChatComposer } from "@/components/ai-assistant/AIChatComposer";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

/**
 * Your AI Office Manager — the platform-level chat assistant, branded as
 * a signature capability of the Elite Business System (815 Digital
 * Services), not a generic utility chatbot. Per design_guidelines.md
 * `ai_office_manager`.
 *
 * This is the ONLY thing pages need to render to get it: it is mounted
 * once, globally, in DashboardLayout.js. Everything else (auth,
 * permissions, business context, provider selection/fallback) is handled
 * server-side per request — this component only renders UI and streams
 * text.
 */
export const DriveDeskAIWidget = () => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [launcherBurst, setLauncherBurst] = useState(false);
  const scrollRef = useRef(null);
  const { user } = useAuth();
  const showTier = isDemoUser(user);
  const {
    messages,
    suggestions,
    sending,
    error,
    sendMessage,
    clearChat,
    approvePlanAction,
    rejectPlanAction,
    actingPlanId,
  } = useAIAssistant({ enabled: open });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Premium "idle ambient" sparkle — every 10-15s the launcher gets a
  // brief, gentle sparkle pop so it stays noticeable without being
  // flashy. Paused while the panel is open (the thinking/typing sparkle
  // takes over attention at that point).
  useEffect(() => {
    if (open) return;
    let popTimeout;
    let scheduleTimeout;
    const scheduleNext = () => {
      const delay = 10000 + Math.random() * 5000;
      scheduleTimeout = setTimeout(() => {
        setLauncherBurst(true);
        popTimeout = setTimeout(() => setLauncherBurst(false), 1400);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => {
      clearTimeout(scheduleTimeout);
      clearTimeout(popTimeout);
    };
  }, [open]);

  const handleOpen = () => {
    // A quick extra sparkle flourish the instant it's opened, on top of
    // whatever ambient pulse is/isn't already running.
    setLauncherBurst(true);
    setOpen(true);
    setTimeout(() => setLauncherBurst(false), 1300);
  };

  const handleSend = () => {
    if (!draft.trim() || sending) return;
    sendMessage(draft);
    setDraft("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isThinking = sending && messages.length > 0 && !messages[messages.length - 1]?.content;

  return (
    <>
      {/* Premium launcher — this IS the approved 815 Digital Services "AI
          Office Manager" artwork (not a recreated icon inside a generic
          FAB shell). Sized ~65% larger than the previous utility-style
          button so it reads as the platform's flagship, unmissable
          capability rather than a small corner utility. Always
          bottom-right, fixed (follows scroll), never covers other
          controls. */}
      <button
        onClick={handleOpen}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[100] h-24 w-24 sm:h-28 sm:w-28 rounded-[28px] shadow-[0_14px_36px_-6px_hsl(var(--ink)/0.55)] hover:shadow-[0_0_0_8px_hsl(var(--gold)/0.16),0_18px_42px_-6px_hsl(var(--gold)/0.55)] hover:scale-[1.045] active:scale-[0.96] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold-bright))] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label="Open Your AI Office Manager"
        data-testid="ai-assistant-launcher"
      >
        <DriveDeskAIIcon className="h-full w-full" size="lg" burst={launcherBurst} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="ai-assistant-panel" hideClose>
          <SheetHeader className="p-4 border-b bg-[hsl(var(--ink))] flex-row items-center justify-between space-y-0">
            <SheetTitle className="flex items-center gap-2 text-base text-white">
              <DriveDeskAIIcon className="h-9 w-9" size="sm" variant="circle" thinking={isThinking} /> Your AI Office Manager
              {showTier && <TierBadge tier="elite" size="xs" tone="emphasis" />}
            </SheetTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                onClick={clearChat} title="Start a new chat" data-testid="ai-assistant-clear-button"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setOpen(false)} data-testid="ai-assistant-close-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <AIChatMessageList
            scrollRef={scrollRef}
            messages={messages}
            suggestions={suggestions}
            sending={sending}
            isThinking={isThinking}
            error={error}
            onPickSuggestion={(p) => sendMessage(p)}
            onApprovePlan={approvePlanAction}
            onRejectPlan={rejectPlanAction}
            actingPlanId={actingPlanId}
          />

          <AIChatComposer
            draft={draft}
            onDraftChange={setDraft}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
            sending={sending}
            suggestions={suggestions}
            hasMessages={messages.length > 0}
            onPickSuggestion={(p) => sendMessage(p)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};
